// Iter AN Phase 2 — orchestrator only. Phase model, UI helpers and AI / save
// / load logic now live in dedicated modules under /pages/newSession/. The
// linear-nav + super-phase routing and per-phase JSX blocks remain here so the
// session flow is easy to reason about end-to-end.
//
// User-flow + testids preserved exactly — anatomy stage, phase ribbon, save
// indicator, sub-step chips, all `super-phase-*` / `sub-step-*` / `tx-*` ids.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSecurity } from "../context/SecurityContext";
import Layout from "../components/Layout";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";

import PhotoUploadSection from "../components/session/PhotoUploadSection";
import SessionSetupSection, { REMIND_EOS_KEY } from "../components/session/SessionSetupSection";
import AnalysisSection from "../components/session/AnalysisSection";
import DiagramSection from "../components/session/DiagramSection";
import WrapUpSection from "../components/session/WrapUpSection";
import SoapSection from "../components/session/SoapSection";
import OutcomeSection from "../components/session/OutcomeSection";
import SignatureCapture from "../components/session/SignatureCapture";
import VoiceTextarea from "../components/VoiceTextarea";
import PreviousVisitPanel from "../components/session/PreviousVisitPanel";
import WhatChangedSection from "../components/session/WhatChangedSection";
import FloatingMicButton from "../components/FloatingMicButton";
import ClinicalReasoningTimeline from "../components/ClinicalReasoningTimeline";
import StickyFooterCTA from "../components/ui/StickyFooterCTA";

// Iter T — clinical-flow building blocks
import SessionLengthPicker from "../components/session/SessionLengthPicker";
import ChiefComplaintPicker from "../components/session/ChiefComplaintPicker";
import PainLevelCard from "../components/session/PainLevelCard";
import RomEditor from "../components/session/RomEditor";
import TherapistFindingsCard from "../components/session/TherapistFindingsCard";
import RecommendationsBundleCard from "../components/session/RecommendationsBundleCard";
import SessionPhotosManager from "../components/SessionPhotosManager";
import { seedRomForArea } from "../lib/clinicalAreas";

// Iter AN Phase 2 — extracted helpers
import { ALL_STEPS, SUPER_FOR, TX_STEPS, TX_LABELS } from "./newSession/constants";
import {
  SaveIndicator, SuperPhaseRibbon, SubStepIndicator,
  PreStepWrapper, LinearNav, TreatmentNotesPanel, RomToggleCard,
} from "./newSession/uiHelpers";
import { useSessionDraft, useStateSnapshot } from "./newSession/hooks/useSessionDraft";
import { useSessionLoad } from "./newSession/hooks/useSessionLoad";
import { useSessionAI } from "./newSession/hooks/useSessionAI";


export default function NewSession() {
  const { id: clientId } = useParams();
  const [params] = useSearchParams();
  const resumeId = params.get("sid");
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const security = useSecurity();

  const [client, setClient] = useState(null);
  const [phase, setPhase] = useState("pre-setup");
  const [step, setStep] = useState(0); // legacy — for photo capture sub-step

  // Photo workflow
  const [sessionType, setSessionType] = useState("follow_up");
  const [photoMode, setPhotoMode] = useState("none");
  const [comparedFromSessionId, setComparedFromSessionId] = useState(null);
  const [photoComparisonSummary, setPhotoComparisonSummary] = useState("");
  const [userOverrodeSessionType, setUserOverrodeSessionType] = useState(false);

  // Iter T — clinical context
  const [chiefComplaintObj, setChiefComplaintObj] = useState({
    chief_complaint: "", chief_complaint_area: null, secondary_concerns: [], issue_type: null,
  });

  const [treatmentMap, setTreatmentMap] = useState(null);

  // Session content
  const [photos, setPhotos] = useState({ front: null, side: null, back: null, left_side: null });
  const [sessionPhotos, setSessionPhotos] = useState({});
  const [signature, setSignature] = useState(null);
  const [uploading, setUploading] = useState({ front: false, side: false, back: false, left_side: false });
  const [intakeNotes, setIntakeNotes] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [workedAreas, setWorkedAreas] = useState([]);
  const [wrapUp, setWrapUp] = useState({ therapist_summary: "", home_care: [], best_return: { when: "", why: "" }, good_return: { when: "", why: "" } });
  const [progressSinceLast, setProgressSinceLast] = useState({
    better: "", worse: "", unchanged: "", new_symptoms: "", resolved_symptoms: "",
    no_significant_changes: false, benefit_duration: "", new_priority: "", finding_updates: [],
  });
  const [previousSession, setPreviousSession] = useState(null);

  // Iter BF — continuation routing. Explicit + persisted with the draft:
  // "continue" (default for returning clients) bypasses the repeated complaint
  // assessment; "full" keeps the normal detailed workflow.
  const [assessmentMode, setAssessmentMode] = useState("continue");
  const continuationActive =
    !!(previousSession && previousSession.id) && assessmentMode !== "full";

  // Carry the established complaint forward as CONTEXT for ROM seeding and the
  // AI treatment-plan generator — the therapist never re-enters it. The old
  // record is read-only; this only populates TODAY's working context.
  useEffect(() => {
    if (!continuationActive || !previousSession?.id) return;
    setChiefComplaintObj((cur) => {
      if (cur.chief_complaint || cur.chief_complaint_area) return cur;
      return {
        chief_complaint: previousSession.chief_complaint || previousSession.intake_notes || "",
        chief_complaint_area: previousSession.chief_complaint_area || null,
        secondary_concerns: previousSession.secondary_concerns || [],
        issue_type: previousSession.issue_type || null,
      };
    });
  }, [continuationActive, previousSession]);
  const [soap, setSoap] = useState({ subjective: "", objective: "", assessment: "", plan: "" });
  const [outcome, setOutcome] = useState({
    pain_before: 0, pain_after: 0, effectiveness: 4, client_feedback: "",
    techniques_used: [], rom_measurements: [], client_reported_improvements: [],
    therapist_findings_summary: "",
    client_text_message: "", clinical_recommendations: [], recommendations_rationale: "",
  });
  const [billing, setBilling] = useState({ cpt_codes: [], icd10_codes: [], date_of_injury: "", case_reference: "", billing_notes: "" });
  const [protocols, setProtocols] = useState([]);
  const [selectedProtocolId, setSelectedProtocolId] = useState(null);
  const [suggestedProtocol, setSuggestedProtocol] = useState(null);
  const [duration, setDuration] = useState(60);
  const [saving, setSaving] = useState(false);
  const [includeRom, setIncludeRom] = useState(false);
  // Returning-client "Pain today" gate — today's pain (outcome.pain_before) must
  // be explicitly set before leaving the "What's changed?" page (0 is a valid
  // score, so we track an explicit interaction rather than a truthy value).
  const [todayPainEntered, setTodayPainEntered] = useState(false);
  const [painError, setPainError] = useState(false);

  // Live Clinical Capture
  const [clinicalCapture, setClinicalCapture] = useState([]);
  const [recurringPatterns, setRecurringPatterns] = useState([]);
  const [externalMicTrigger, setExternalMicTrigger] = useState(0);
  const [externalMicMuscle, setExternalMicMuscle] = useState(null);
  const [externalMicSide, setExternalMicSide] = useState(null);
  const [micActive, setMicActive] = useState(false);

  // Auto-save hook
  const {
    sessionId, setSessionId,
    saveStatus, setSaveStatus, savedAt, setSavedAt,
    saveDraft, setStateSnapshot,
  } = useSessionDraft({ clientId, resumeId });

  // Treatment notes shorthand — persisted into wrap_up.therapist_summary so
  // existing AI suggestions (SOAP, wrap-up, recommendations) keep working.
  const treatmentNotes = wrapUp.therapist_summary || "";
  const setTreatmentNotes = useCallback(
    (v) => setWrapUp((w) => ({ ...w, therapist_summary: typeof v === "function" ? v(w.therapist_summary || "") : v })),
    []
  );

  // Keep saveDraft()'s snapshot mirror up to date every render
  useStateSnapshot(setStateSnapshot, {
    sessionId, photos, sessionPhotos, intakeNotes, analysis, workedAreas, wrapUp, progressSinceLast,
    soap, outcome, billing, selectedProtocolId, duration,
    sessionType, photoMode, comparedFromSessionId, photoComparisonSummary,
    treatmentMap, chiefComplaintObj, assessmentMode,
  });

  // Initial load (+ optional resume)
  useSessionLoad(
    { clientId, resumeId, refreshUser },
    {
      setClient, setProtocols, setRecurringPatterns, setPreviousSession,
      setSessionId, setSessionType, setUserOverrodeSessionType,
      setPhotoMode, setComparedFromSessionId, setPhotoComparisonSummary,
      setTreatmentMap, setPhotos, setSessionPhotos, setSignature,
      setIntakeNotes, setAnalysis, setWorkedAreas, setWrapUp,
      setProgressSinceLast, setSoap, setOutcome, setBilling,
      setSelectedProtocolId, setDuration, setClinicalCapture,
      setChiefComplaintObj, setPhase, setSavedAt, setSaveStatus,
      setIncludeRom, setAssessmentMode,
    }
  );

  // AI callbacks
  const ai = useSessionAI(
    {
      client, previousSession, progressSinceLast, clinicalCapture,
      photos, intakeNotes, chiefComplaintObj, workedAreas, analysis,
      wrapUp, outcome, clientId, phase, suggestedProtocol,
    },
    {
      setAnalysis, setWorkedAreas, setOutcome, setPhase, saveDraft,
      setSoap, setSuggestedProtocol, setSelectedProtocolId, setProtocols,
      setPhotoComparisonSummary, setWrapUp,
    }
  );

  const handleManualSessionType = useCallback((v) => {
    setSessionType(v);
    setUserOverrodeSessionType(true);
  }, []);
  const handleAutoDetectedSessionType = useCallback((v) => {
    setSessionType((cur) => (userOverrodeSessionType ? cur : v));
  }, [userOverrodeSessionType]);

  const onSelectFile = async (e, key) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Use JPEG, PNG, or WEBP");
      return;
    }
    setUploading((u) => ({ ...u, [key]: true }));
    try {
      const form = new FormData();
      form.append("file", f);
      const r = await api.post("/photos/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => ({ ...p, [key]: r.data.path }));
      setTimeout(() => saveDraft(), 0);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading((u) => ({ ...u, [key]: false }));
    }
  };

  const allPhotosDone = photos.front && photos.side && photos.back && photos.left_side;

  // Final save → mark as completed
  const save = async () => {
    setSaving(true);
    try {
      await saveDraft({ status: "completed" });
      try { localStorage.removeItem(REMIND_EOS_KEY(clientId)); } catch { /* ignore */ }
      toast.success("Session saved");
      navigate(`/clients/${clientId}`);
    } catch {
      toast.error("Could not finalize session");
    } finally {
      setSaving(false);
    }
  };

  // Iter BE — "Remind me later" resurfaces the photo checkpoint before completion.
  const deferredMilestone = useMemo(() => {
    try { return parseInt(localStorage.getItem(REMIND_EOS_KEY(clientId)) || "0", 10) || null; } catch { return null; }
  }, [clientId, phase]); // eslint-disable-line react-hooks/exhaustive-deps
  const showDeferredPhotoBanner = phase === "post-save" && deferredMilestone && !photos.front && !photos.side && !photos.back;
  const skipDeferredCheckpoint = async () => {
    try { await api.post(`/clients/${clientId}/photo-checkpoint/skip`, { milestone: deferredMilestone }); } catch { /* non-fatal */ }
    try { localStorage.removeItem(REMIND_EOS_KEY(clientId)); } catch { /* ignore */ }
    goToPhase("post-save");
  };

  // Re-attach previous photos when "compare to previous" was picked at setup.
  const enterIntakeWithPreviousPhotos = async () => {
    if (!comparedFromSessionId) return;
    try {
      const r = await api.get(`/sessions/${comparedFromSessionId}`);
      const prev = r.data;
      setPhotos({ front: prev.front_image, side: prev.side_image, back: prev.back_image, left_side: prev.photos?.baseline_left_side || null });
      if (prev.ai_analysis) setAnalysis(prev.ai_analysis);
    } catch {
      toast.error("Could not load previous photos");
    }
  };

  // Seed ROM rows when the chief complaint area changes, if rows are still
  // empty — only when the therapist has opted into ROM for this session.
  const onChiefComplaintChange = useCallback((next) => {
    setChiefComplaintObj(next);
    if (next.chief_complaint_area && includeRom) {
      setOutcome((o) => {
        const cur = o.rom_measurements || [];
        if (cur.length === 0) return { ...o, rom_measurements: seedRomForArea(next.chief_complaint_area, []) };
        return o;
      });
    }
    setTimeout(() => saveDraft(), 0);
  }, [saveDraft, includeRom]);

  // ROM is opt-in: toggling it on seeds area-tailored rows if none exist yet.
  const handleIncludeRomChange = useCallback((on) => {
    setIncludeRom(on);
    if (on && chiefComplaintObj.chief_complaint_area) {
      setOutcome((o) => {
        const cur = o.rom_measurements || [];
        if (cur.length === 0) return { ...o, rom_measurements: seedRomForArea(chiefComplaintObj.chief_complaint_area, []) };
        return o;
      });
    }
  }, [chiefComplaintObj.chief_complaint_area]);

  const goToPhase = (next) => {
    setPhase(next);
    setTimeout(() => saveDraft(), 0);
  };
  const isSkippedStep = (p) =>
    (p === "pre-changes" && !(previousSession && previousSession.id)) ||
    (p === "pre-complaint" && continuationActive) ||
    (p === "pre-photos" && photoMode === "none") ||
    ((p === "pre-rom" || p === "post-rom") && !includeRom);
  const linearNext = () => {
    const idx = ALL_STEPS.indexOf(phase);
    if (idx >= 0 && idx < ALL_STEPS.length - 1) {
      let nextIdx = idx + 1;
      while (isSkippedStep(ALL_STEPS[nextIdx]) && nextIdx < ALL_STEPS.length - 1) nextIdx += 1;
      goToPhase(ALL_STEPS[nextIdx]);
    }
  };
  const linearBack = () => {
    const idx = ALL_STEPS.indexOf(phase);
    if (idx > 0) {
      let prevIdx = idx - 1;
      while (isSkippedStep(ALL_STEPS[prevIdx]) && prevIdx > 0) prevIdx -= 1;
      goToPhase(ALL_STEPS[prevIdx]);
    }
  };

  const TX_TAB_CHIPS = useMemo(() => TX_STEPS.map((k) => ({ key: k, label: TX_LABELS[k] })), []);
  const sup = SUPER_FOR(phase);
  const showFloatingMic = sessionId && (sup === "tx" || sup === "eot" || phase === "post-pain" || phase === "post-rom");

  return (
    <Layout>
      <Link to={`/clients/${clientId}`} className="inline-flex items-center gap-2 text-sm text-ink-700 hover:text-ink-900 mb-6" data-testid="back-to-client">
        <ArrowLeft className="w-4 h-4" /> Back to {client?.name || "client"}
      </Link>

      <PreviousVisitPanel previous={previousSession} />

      {/* Phase ribbon + save indicator */}
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap" data-testid="phase-progress">
        <SuperPhaseRibbon phase={phase} onJump={goToPhase} />
        <SaveIndicator status={saveStatus} savedAt={savedAt} />
      </div>

      <SubStepIndicator phase={phase} onJump={goToPhase} photoMode={photoMode} includeRom={includeRom} hasPrevious={!!(previousSession && previousSession.id)} continuation={continuationActive} />

      <div className="mt-6">
        {phase === "pre-setup" && (
          <SessionSetupSection
            clientId={clientId}
            sessionType={sessionType}
            setSessionType={handleManualSessionType}
            photoMode={photoMode}
            setPhotoMode={(m) => {
              setPhotoMode(m);
              if (m !== "previous") {
                setComparedFromSessionId(null);
                setPhotoComparisonSummary("");
              }
            }}
            comparedFromSessionId={comparedFromSessionId}
            setComparedFromSessionId={setComparedFromSessionId}
            setPhase={(next) => {
              setPhase(previousSession && previousSession.id ? "pre-changes" : "pre-length");
              (async () => {
                try {
                  await saveDraft();
                  if (next === "intake" && photoMode === "previous") await enterIntakeWithPreviousPhotos();
                } catch {
                  // Background save failure is non-fatal — user can re-save explicitly.
                }
              })();
            }}
            onAutoDetected={handleAutoDetectedSessionType}
            hasPrevious={!!(previousSession && previousSession.id)}
            assessmentMode={assessmentMode}
            setAssessmentMode={setAssessmentMode}
          />
        )}

        {phase === "pre-changes" && (
          <PreStepWrapper
            title="What's changed since last time?"
            subtitle="Posturra already knows this client — only capture what's different today. One tap if nothing significant changed."
          >
            <div data-testid="pain-today-wrap">
              <PainLevelCard
                label="Pain today"
                description="How is their primary concern feeling right now?"
                value={outcome.pain_before}
                onChange={(v) => {
                  setOutcome((o) => ({ ...o, pain_before: v }));
                  setTodayPainEntered(true);
                  setPainError(false);
                  setTimeout(() => saveDraft(), 0);
                }}
                accent="terracotta" testIdPrefix="pre-pain"
                compareTo={todayPainEntered ? (previousSession?.outcome?.pain_before ?? previousSession?.outcome?.pain_after ?? null) : null}
                compareLabel="vs last visit"
              />
              {painError && (
                <p className="mt-2 text-sm font-medium text-terracotta-600" data-testid="pain-today-error">
                  Enter today's pain score (0–10) before continuing.
                </p>
              )}
            </div>
            <WhatChangedSection
              previousSession={previousSession}
              progress={progressSinceLast}
              onChange={setProgressSinceLast}
              onBlurSave={() => saveDraft()}
              painEntered={todayPainEntered}
              onPainRequired={() => {
                setPainError(true);
                document.querySelector('[data-testid="pain-today-wrap"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
              }}
              onNoChanges={() => { setTimeout(() => saveDraft(), 0); linearNext(); }}
            />
            <LinearNav
              onBack={linearBack}
              onNext={() => { if (!todayPainEntered) { setPainError(true); document.querySelector('[data-testid="pain-today-wrap"]')?.scrollIntoView({ behavior: "smooth", block: "center" }); return; } linearNext(); }}
              nextLabel="Continue"
            />
          </PreStepWrapper>
        )}

        {phase === "pre-length" && (
          <PreStepWrapper title="How long is today's session?" subtitle="This anchors the cadence — and starts the secure treatment window (session length + your documentation grace period).">
            <SessionLengthPicker value={duration} onChange={setDuration} onSaveDraft={() => saveDraft()} />
            <LinearNav
              onBack={linearBack}
              onNext={() => { security?.startWindow(sessionId, duration); linearNext(); }}
              nextLabel="Start session"
            />
          </PreStepWrapper>
        )}

        {phase === "pre-complaint" && (
          <PreStepWrapper
            title="What brought them in today?"
            subtitle="Pick the primary area first — the AI analysis and treatment mapping will be tailored to that area."
          >
            <PainLevelCard
              label="Pain level right now (0–10)"
              value={outcome.pain_before}
              onChange={(v) => setOutcome((o) => ({ ...o, pain_before: v }))}
              accent="terracotta" testIdPrefix="pre-pain"
            />
            <ChiefComplaintPicker value={chiefComplaintObj} onChange={onChiefComplaintChange} onSaveDraft={() => saveDraft()} />
            <RomToggleCard checked={includeRom} onChange={handleIncludeRomChange} testId="include-rom-toggle" />
            <LinearNav
              onBack={linearBack} onNext={linearNext}
              nextDisabled={!chiefComplaintObj.chief_complaint_area}
              nextLabel={photoMode === "none" ? (includeRom ? "Continue to baseline ROM" : "Start treatment") : "Continue to photos & intake"}
            />
          </PreStepWrapper>
        )}

        {phase === "pre-photos" && (
          <PreStepWrapper title="Photos & intake" subtitle="Capture posture photos and record what the client described — the AI uses both.">
            {photoMode === "new" && (
              <PhotoUploadSection
                step={step} setStep={setStep}
                photos={photos} uploading={uploading}
                onSelectFile={onSelectFile} runAnalysis={ai.runAnalysis}
                allPhotosDone={allPhotosDone}
              />
            )}

            <div className="mt-6 bg-white border border-paper-200 rounded-3xl p-6 sm:p-8">
              <div className="label-overline mb-2 text-terracotta-600">Verbatim intake (in their words)</div>
              <VoiceTextarea
                value={intakeNotes} onChange={setIntakeNotes}
                onBlur={() => saveDraft()} rows={4}
                placeholder={'"Pain started Wednesday after lifting a box. Worse in the morning. Can\'t turn head fully to the right."'}
                testId="intake-notes-input"
              />
            </div>

            {/* "What changed?" now lives in its own pre-changes step for returning clients */}

            {photoMode === "previous" && (
              <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8 mt-6">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                  <div>
                    <div className="label-overline mb-1 text-sage-600">Comparison summary</div>
                    <h2 className="font-heading text-lg text-ink-900">AI comparison narrative</h2>
                  </div>
                  <button
                    onClick={() => ai.runPhotoComparison(comparedFromSessionId)}
                    disabled={ai.generatingComparison}
                    data-testid="run-photo-comparison-btn"
                    className="min-h-[44px] px-5 rounded-full bg-ink-900 text-white text-sm font-medium hover:bg-ink-800 disabled:opacity-50"
                  >
                    {ai.generatingComparison ? "Generating…" : photoComparisonSummary ? "Regenerate" : "Generate comparison"}
                  </button>
                </div>
                <textarea
                  value={photoComparisonSummary}
                  onChange={(e) => setPhotoComparisonSummary(e.target.value)}
                  onBlur={() => saveDraft()} rows={4}
                  placeholder="Tap 'Generate comparison' or type your own observation."
                  data-testid="photo-comparison-textarea"
                  className="w-full px-3 py-2 rounded-2xl border border-paper-200 text-sm"
                />
              </div>
            )}

            {analysis && photoMode === "new" && (
              <div className="mt-6">
                <AnalysisSection
                  analysis={analysis} setPhase={() => {}}
                  suggestProtocol={ai.suggestProtocol} suggestingProtocol={ai.suggestingProtocol}
                  suggestedProtocol={suggestedProtocol} applyProtocol={ai.applyProtocol}
                  saveSuggestedProtocol={ai.saveSuggestedProtocol}
                  hideNav
                />
              </div>
            )}

            <LinearNav onBack={linearBack} onNext={linearNext} nextLabel={includeRom ? "Continue to baseline ROM" : "Start treatment"} />
          </PreStepWrapper>
        )}

        {phase === "pre-photos-analyzing" && (
          <div className="py-20 text-center" data-testid="analyzing-state">
            <div className="inline-block w-16 h-16 rounded-full border-4 border-sage-500 border-t-transparent animate-spin mb-6" />
            <h2 className="font-heading text-2xl font-light text-ink-900 mb-2">Analyzing posture…</h2>
            <p className="text-ink-700">Claude Sonnet 4.5 is reviewing the four photos {intakeNotes ? "and your intake notes" : ""}. Usually under 20 seconds.</p>
          </div>
        )}

        {phase === "pre-rom" && (
          <PreStepWrapper
            title="Baseline range of motion"
            subtitle="Measure the relevant tests now — you'll re-test the exact same movements after treatment."
          >
            <RomEditor value={outcome.rom_measurements || []} onChange={(rom) => setOutcome((o) => ({ ...o, rom_measurements: rom }))} />
            <LinearNav onBack={linearBack} onNext={linearNext} nextLabel="Start treatment" />
          </PreStepWrapper>
        )}

        {/* ── TREATMENT (freely navigable tabs) ────────────────────────── */}
        {sup === "tx" && (
          <div data-testid="treatment-super">
            <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="tx-tabs">
              {TX_TAB_CHIPS.map((c) => (
                <button
                  key={c.key} type="button"
                  onClick={() => goToPhase(c.key)}
                  data-testid={`tx-tab-${c.key}`}
                  className={`min-h-[40px] px-4 rounded-full text-xs font-bold uppercase tracking-[0.12em] transition-colors ${
                    phase === c.key ? "bg-sage-500 text-white" : "bg-paper-100 text-ink-500 hover:bg-paper-200"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>

            {phase === "tx-notes" && (
              <TreatmentNotesPanel value={treatmentNotes} onChange={setTreatmentNotes} onSaveDraft={() => saveDraft()} />
            )}

            {phase === "tx-diagram" && (
              <DiagramSection
                workedAreas={workedAreas} setWorkedAreas={setWorkedAreas} analysis={analysis}
                setPhase={(p) => {
                  if (p === "soap" || p === "wrapup") return goToPhase("eot-recs");
                  if (p === "analysis") return goToPhase("pre-photos");
                  return goToPhase(p);
                }}
                clientId={clientId} client={client} sessionId={sessionId}
                sessionType={sessionType}
                intakeNotes={intakeNotes || chiefComplaintObj.chief_complaint}
                previousSession={previousSession} progressSinceLast={progressSinceLast}
                clinicalSnapshot={ai.buildContinuity().clinical_snapshot}
                treatmentMap={treatmentMap}
                setTreatmentMap={(m) => { setTreatmentMap(m); setTimeout(() => saveDraft({ treatment_map: m }), 0); }}
                onMuscleLongPress={(muscleId) => {
                  const side = /-left$|_left$/.test(muscleId) ? "left"
                             : /-right$|_right$/.test(muscleId) ? "right"
                             : null;
                  setExternalMicMuscle(muscleId);
                  setExternalMicSide(side);
                  setExternalMicTrigger((n) => n + 1);
                  toast.message(`Listening for ${muscleId.replace(/[-_]/g, " ")}…`);
                }}
                clinicalCapture={clinicalCapture}
                onWorkspaceMicTrigger={() => {
                  setExternalMicMuscle(null); setExternalMicSide(null);
                  setExternalMicTrigger((n) => n + 1);
                }}
                micActive={micActive}
              />
            )}

            {phase === "tx-capture" && (
              <div data-testid="tx-capture-panel">
                <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8 mb-6">
                  <div className="label-overline mb-2 text-sage-700">Live treatment capture</div>
                  <h2 className="font-heading text-2xl text-ink-900 mb-2">Speak findings as you work</h2>
                  <p className="text-ink-700 text-sm mb-4">
                    Tap the floating mic (bottom-right) or long-press a muscle on the Diagram tab. Each capture is auto-structured into the timeline below and informs the AI recommendations later.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setExternalMicMuscle(null); setExternalMicSide(null);
                      setExternalMicTrigger((n) => n + 1);
                    }}
                    data-testid="tx-capture-mic-btn"
                    className="min-h-[48px] px-6 rounded-full bg-ink-900 text-white font-medium hover:bg-ink-800 inline-flex items-center gap-2"
                  >
                    {micActive ? "Recording…" : "Start a new capture"}
                  </button>
                </div>
                {(clinicalCapture.length > 0 || recurringPatterns.length > 0) ? (
                  <div>
                    <div className="label-overline mb-3 text-sage-700">Clinical reasoning timeline · today&rsquo;s captures</div>
                    <ClinicalReasoningTimeline
                      captures={clinicalCapture} sessionId={sessionId}
                      onChange={() => api.get(`/sessions/${sessionId}`).then((r) => setClinicalCapture(r.data.clinical_capture || []))}
                      recurringPatterns={recurringPatterns}
                    />
                  </div>
                ) : (
                  <div className="text-center text-ink-500 text-sm py-8 border border-dashed border-paper-300 rounded-3xl" data-testid="tx-capture-empty">
                    No captures yet. Start the mic and speak naturally — &ldquo;Right upper trap dense, releases with sustained pressure&rdquo; works perfectly.
                  </div>
                )}
              </div>
            )}

            <StickyFooterCTA testid="tx-footer" className="mt-8 justify-between">
              <button type="button" onClick={linearBack} className="min-h-[48px] px-5 rounded-full text-ink-700 hover:bg-paper-100 inline-flex items-center gap-2" data-testid="tx-back-btn">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                type="button" onClick={() => goToPhase("post-pain")}
                data-testid="tx-to-eot-btn"
                className="min-h-[48px] px-6 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-medium inline-flex items-center gap-2"
              >
                <span className="whitespace-nowrap">End treatment → Pain reassessment</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </StickyFooterCTA>
          </div>
        )}

        {/* ── END-OF-TREATMENT ───────────────────────────────────────── */}
        {phase === "eot-findings" && (
          <PreStepWrapper
            title="Therapist clinical findings"
            subtitle="Capture these while the client is still on the table — the ending pain score you just recorded plus these observations ground the AI recommendations you're about to generate."
          >
            <TherapistFindingsCard
              findings={outcome.therapist_findings}
              onChange={(tf) => setOutcome((o) => ({ ...o, therapist_findings: { ...(tf || {}), captured_at: new Date().toISOString() } }))}
              onSaveDraft={() => saveDraft()}
            />
            <LinearNav
              onBack={linearBack} onNext={linearNext}
              nextDisabled={!(outcome.therapist_findings && (outcome.therapist_findings.overall_improvement || (outcome.therapist_findings.assessment_notes || "").trim()))}
              nextLabel="Continue → Generate recommendations"
            />
          </PreStepWrapper>
        )}

        {phase === "eot-recs" && (
          <PreStepWrapper
            title="AI recommendations"
            subtitle="One tap produces the SOAP-style summary, home care, stretches, client message, and share card — using the ending pain score you just captured."
          >
            <RecommendationsBundleCard
              sessionId={sessionId}
              recommendations={outcome.recommendations}
              outcome={outcome}
              onGenerated={(bundle) => setOutcome((o) => ({ ...o, recommendations: bundle }))}
              // Iter AO — verbal debrief is the single source of truth. The AI
              // can also fall back to clinical findings or any worked areas if
              // the therapist skipped the debrief entirely.
              hasFindings={
                !!(treatmentNotes || "").trim() ||
                !!(outcome.therapist_findings && (outcome.therapist_findings.overall_improvement || (outcome.therapist_findings.assessment_notes || "").trim())) ||
                (workedAreas || []).length > 0
              }
              autoTrigger
              client={client}
            />

            {/* Quick read-only echo of the single verbal debrief so the
                therapist sees the AI input source without being asked again. */}
            {(treatmentNotes || "").trim() && (
              <details className="mt-4 bg-paper-50 border border-paper-200 rounded-3xl p-4" data-testid="eot-debrief-echo">
                <summary className="cursor-pointer text-xs font-medium text-ink-700 hover:text-ink-900 inline-flex items-center gap-2">
                  Verbal debrief used as the AI source
                </summary>
                <p className="mt-3 text-sm text-ink-900 whitespace-pre-line leading-relaxed">{treatmentNotes}</p>
              </details>
            )}

            <details className="mt-6 bg-paper-50 border border-paper-200 rounded-3xl p-5" data-testid="eot-advanced-toggle">
              <summary className="cursor-pointer font-medium text-ink-700 hover:text-ink-900 inline-flex items-center gap-2">
                Advanced / Edit · SOAP &amp; Wrap-up
              </summary>
              <div className="mt-4 space-y-6">
                <WrapUpSection
                  wrapUp={wrapUp} setWrapUp={setWrapUp}
                  setPhase={() => {}}
                />
                <SoapSection
                  soap={soap} setSoap={setSoap}
                  setPhase={() => {}}
                />
              </div>
            </details>

            <LinearNav onBack={linearBack} onNext={linearNext} nextLabel="Movement photos (optional)" />
          </PreStepWrapper>
        )}

        {/* ── POST-TREATMENT ─────────────────────────────────────────── */}
        {phase === "post-pain" && (
          <PreStepWrapper
            title="Pain reassessment"
            subtitle="Ask the client to rate their pain right now — the ending score feeds straight into your findings and the AI recommendations."
            postPhase
          >
            <PainLevelCard
              label="Pain level right now (0–10)"
              value={outcome.pain_after}
              onChange={(v) => setOutcome((o) => ({ ...o, pain_after: v }))}
              accent="sage" testIdPrefix="post-pain" compareTo={outcome.pain_before}
            />
            <RomToggleCard checked={includeRom} onChange={handleIncludeRomChange} testId="include-rom-toggle-post" />
            <LinearNav onBack={linearBack} onNext={linearNext} nextLabel={includeRom ? "Reassess ROM" : "Clinical findings"} />
          </PreStepWrapper>
        )}

        {phase === "post-rom" && (
          <PreStepWrapper
            title="ROM reassessment"
            subtitle="Re-test the same movements you measured at baseline. Only the 'after' column is editable."
            postPhase
          >
            <RomEditor value={outcome.rom_measurements || []} onChange={(rom) => setOutcome((o) => ({ ...o, rom_measurements: rom }))} />
            <LinearNav onBack={linearBack} onNext={linearNext} nextLabel="Clinical findings" />
          </PreStepWrapper>
        )}

        {phase === "post-photos" && (
          <PreStepWrapper
            title="Movement photos (optional)"
            subtitle="Capture post-treatment posture or movement photos while the client is standing dressed. Skip if not needed."
            postPhase
          >
            {sessionId ? (
              <SessionPhotosManager
                session={{ id: sessionId, photos: sessionPhotos, front_image: photos.front?.path, back_image: photos.back?.path, side_image: photos.side?.path }}
                onSessionUpdate={(s) => { if (s.photos) setSessionPhotos(s.photos); }}
                phases={["post"]}
              />
            ) : (
              <div className="bg-paper-50 border border-paper-200 rounded-3xl p-6 text-sm text-ink-700" data-testid="post-photos-no-session">
                Save the draft first to enable photo upload.
              </div>
            )}
            <LinearNav onBack={linearBack} onNext={linearNext} nextLabel="Review & save" />
          </PreStepWrapper>
        )}

        {phase === "post-save" && (
          <div data-testid="post-save-phase">
            {showDeferredPhotoBanner && (
              <div className="rounded-3xl border border-terracotta-500/40 bg-terracotta-500/5 p-5 mb-6 flex items-start gap-3" data-testid="deferred-photo-banner">
                <div className="flex-1">
                  <div className="font-medium text-ink-900 mb-1">Progress photos still due</div>
                  <div className="text-sm text-ink-700 mb-3">You asked to be reminded — it's Session {deferredMilestone} and no photos were taken yet.</div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      data-testid="deferred-photo-take"
                      onClick={() => { setPhotoMode("new"); goToPhase("pre-photos"); }}
                      className="min-h-[36px] px-4 rounded-full bg-ink-900 text-white text-xs font-medium hover:bg-ink-800"
                    >
                      Take photos now
                    </button>
                    <button
                      data-testid="deferred-photo-skip"
                      onClick={skipDeferredCheckpoint}
                      className="min-h-[36px] px-4 rounded-full bg-white border border-paper-200 text-ink-700 text-xs hover:bg-paper-100"
                    >
                      Skip this checkpoint
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="label-overline mb-2">Reassessment &middot; 4 of 4</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-ink-900 mb-2">Review &amp; save</h1>
            <p className="text-ink-700 mb-6 max-w-2xl">
              Capture what changed today, confirm billing details
              {user?.profile?.signature_capture_enabled ? ", and (optionally) collect the client\u2019s signature" : ""}
              {" "}before saving.
            </p>

            {/* Iter AK + AM — gated patient signature pad */}
            {sessionId && user?.profile?.signature_capture_enabled && (
              <div className="mb-6" data-testid="post-save-signature-block">
                <SignatureCapture
                  sessionId={sessionId} signature={signature}
                  clientName={client?.name || ""}
                  onSaved={(sig) => setSignature(sig)}
                  onCleared={() => setSignature(null)}
                />
              </div>
            )}

            <OutcomeSection
              outcome={outcome} setOutcome={setOutcome}
              analysis={analysis} user={user}
              billing={billing} setBilling={setBilling}
              protocols={protocols} selectedProtocolId={selectedProtocolId}
              setSelectedProtocolId={setSelectedProtocolId}
              duration={duration} setDuration={setDuration}
              save={save} saving={saving}
              setPhase={(p) => p === "soap" ? goToPhase("eot-recs") : goToPhase(p)}
              sessionId={sessionId} hideTopHeader hidePainSliders
            />
          </div>
        )}
      </div>

      {showFloatingMic && (
        <FloatingMicButton
          sessionId={sessionId}
          onSaved={(cap) => {
            setClinicalCapture((prev) => [...prev, cap]);
            if (clientId) {
              api.get(`/clients/${clientId}/recurring-patterns?threshold=3`).then((r) => setRecurringPatterns(r.data)).catch(() => {});
            }
          }}
          preTaggedMuscle={externalMicMuscle}
          preTaggedSide={externalMicSide}
          openFromExternalTrigger={externalMicTrigger > 0}
          onExternalConsumed={() => { setExternalMicTrigger(0); setExternalMicMuscle(null); setExternalMicSide(null); }}
          onActiveChange={setMicActive}
        />
      )}
    </Layout>
  );
}
